import { useEffect, useState } from 'react';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import MarkdownEditor from '../../components/admin/MarkdownEditor';
import { useConfirm } from '../../components/admin/useConfirm';
import { SUBJECTS, SUBJECT_LABEL } from './adminConstants';

type Book = { id: string; title: string; subject: number; chunkCount: number; createdAt: string };
const emptyForm = { book: "", subject: 0, chapter: "", text: "" };

function fmt(d: string) { const x = new Date(d); return x.getDate() + ". " + (x.getMonth() + 1) + ". " + x.getFullYear(); }

export default function AdminLibrary() {
    const [books, setBooks] = useState<Book[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [form, setForm] = useState({ ...emptyForm });
    const [busy, setBusy] = useState(false);
    const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
    const api = new ApiHelper();
    const { ask, confirmModal } = useConfirm();

    useEffect(() => { load(); }, []);
    async function load() {
        try { setBooks(await (await api.adminLibraryApi.books()).json()); }
        catch (x: any) { api.adminError(x); }
        finally { setLoaded(true); }
    }

    async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const text = await f.text();
            const title = f.name.replace(/\.(md|markdown|txt)$/i, "");
            setForm({ ...form, text, book: form.book.trim() ? form.book : title });
            api.alert.success("Soubor „" + f.name + "\" načten – zkontroluj a nahraj do knihovny");
        } catch (x: any) { api.adminError(x); }
        finally { e.target.value = ""; }
    }

    async function ingest() {
        if (!form.book.trim() || !form.text.trim()) { api.alert.error("Vyplň název knihy a text"); return; }
        setBusy(true);
        try {
            const r = await (await api.adminLibraryApi.ingest({ book: form.book.trim(), subject: form.subject, chapter: form.chapter.trim(), text: form.text })).json();
            api.alert.success("Nahráno " + r.chunks + " částí do „" + r.book + "\"");
            setForm({ ...emptyForm, book: form.book, subject: form.subject });
            await load();
        } catch (x: any) { api.adminError(x); } finally { setBusy(false); }
    }

    async function saveRename() {
        if (!renaming) return;
        const title = renaming.title.trim();
        if (!title) { api.alert.error("Zadej název"); return; }
        try { await api.adminLibraryApi.rename(renaming.id, { title }); setRenaming(null); await load(); }
        catch (x: any) { api.adminError(x); }
    }

    function del(b: Book) {
        ask(<>Smazat knihu „{b.title}" včetně jejích vektorů z knihovny AI tutora? Tuto akci nelze vrátit.</>,
            async () => { try { await api.adminLibraryApi.remove(b.id); await load(); } catch (x: any) { api.adminError(x); } },
            { title: "Smazat knihu?" });
    }

    return (
        <div>
            <div className="adminHead">
                <div><h1>Knihovna</h1><p>Učebnice pro AI tutora (RAG) — nahrávání a katalog</p></div>
            </div>

            <div className="aCard" style={{ marginBottom: "18px" }}>
                <h2>Nahrát text do knihovny</h2>
                <div className="inlineRow">
                    <div className="aField inline"><label>Kniha</label><input value={form.book} onChange={e => setForm({ ...form, book: e.target.value })} placeholder="Název učebnice" /></div>
                    <div className="aField inline"><label>Předmět</label><select value={form.subject} onChange={e => setForm({ ...form, subject: Number(e.target.value) })}>{SUBJECTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}</select></div>
                    <div className="aField inline"><label>Kapitola</label><input value={form.chapter} onChange={e => setForm({ ...form, chapter: e.target.value })} placeholder="Např. Buňka" /></div>
                </div>
                <div className="aField">
                    <label>Import .md souboru</label>
                    <div className="uploadField"><input type="file" accept=".md,.markdown,.txt,text/markdown" onChange={onPickFile} /></div>
                    <span className="noneHint">Soubor se načte do pole níže – text se při nahrání rozdělí na části a zaindexuje (embedding).</span>
                </div>
                <div className="aField"><label>Text</label><MarkdownEditor value={form.text} onChange={t => setForm({ ...form, text: t })} height={420} placeholder="Vlož text kapitoly nebo importuj .md soubor…" /></div>
                <button className="aBtn" disabled={busy} onClick={ingest}>{busy ? "Nahrávám…" : "Nahrát do knihovny"}</button>
            </div>

            <table className="aTable">
                <thead><tr><th>Kniha</th><th>Předmět</th><th>Částí</th><th>Vytvořeno</th><th></th></tr></thead>
                <tbody>
                    {books.map(b => (
                        <tr key={b.id}>
                            <td>{renaming?.id === b.id
                                ? <input className="inlineEdit" autoFocus value={renaming.title} onChange={e => setRenaming({ id: b.id, title: e.target.value })}
                                    onKeyDown={e => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenaming(null); }} />
                                : b.title}</td>
                            <td>{SUBJECT_LABEL[b.subject]}</td>
                            <td>{b.chunkCount}</td>
                            <td>{fmt(b.createdAt)}</td>
                            <td>
                                {renaming?.id === b.id
                                    ? <><span className="actBtn" title="Uložit" onClick={saveRename}>✓</span><span className="actBtn" title="Zrušit" onClick={() => setRenaming(null)}>×</span></>
                                    : <><span className="actBtn" title="Přejmenovat" onClick={() => setRenaming({ id: b.id, title: b.title })}>✎</span><span className="actBtn del" title="Smazat" onClick={() => del(b)}>×</span></>}
                            </td>
                        </tr>
                    ))}
                    {loaded && books.length === 0 && <tr><td colSpan={5} className="muted">Knihovna je prázdná.</td></tr>}
                </tbody>
            </table>
            {confirmModal}
        </div>
    );
}
